import axios, { AxiosError } from "axios";
import { toast } from "../components/ui/use-toast";
import { PLACEHOLDER_DOCUMENT_ID } from "../consts";
import { Document, DocumentUpdate } from "../types";
import {
  BASE_URL,
  getAuthToken,
  getGoogleRefreshToken,
  getUserEmail,
  removeAuthToken,
  removeGoogleAccessToken,
  removeUserEmail,
  setAuthToken,
} from "./auth";
import { googleAccessToken } from "./googleAccessToken";

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    if (response.status === 204) {
      toast({ title: "Success", description: "Delete successful" });
      return response;
    }
    if (!response || !response.data) {
      toast({
        title: "Error",
        description: "No data received",
        variant: "destructive",
      });
      response.data = null;
      return response;
    }
    return response;
  },
  async (error: AxiosError) => {
    try {
      if (
        (error.response &&
          error.response.status >= 400 &&
          error.response.data) ||
        error.code === "ERR_NETWORK"
      ) {
        if (error?.response?.status === 401 || error.code === "ERR_NETWORK") {
          console.log(error.response);
          const refreshToken = getGoogleRefreshToken();
          const email = getUserEmail();
          if (refreshToken && email) {
            const googleAccessTkn = await googleAccessToken();
            if (googleAccessTkn) {
              const params = {
                email,
                access_token: googleAccessTkn,
                google_document_id: PLACEHOLDER_DOCUMENT_ID,
              };
              const tokenCall = await axios.get(
                BASE_URL +
                  "/auth/token/access-token?" +
                  new URLSearchParams(params).toString()
              );
              const { access_token } = tokenCall.data;
              setAuthToken(access_token);
              if (error.config) {
                window.location.reload();
              } else {
                window.location.href = "/#redirected";
              }
            }
          } else {
            removeAuthToken();
            removeUserEmail();
            removeGoogleAccessToken();
            window.location.href = "/login#redirected";
          }
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "An error occurred",
        variant: "destructive",
      });
      return Promise.reject(error);
    }
  }
);

export const getOrganization = () => {
  return null;
};
export const getDocuments = () =>
  api.get<{ library: Document; templates: Document[] }>("/dash/documents");
export const updateDocument = (docId: string, data: DocumentUpdate) =>
  api.put<{ success: boolean }>(`/dash/documents/${docId}`, data);
export const deleteTemplate = (docId: string) =>
  api.delete(`/dash/documents/${docId}`);
export const submitDocumentUpdate = (docId: string, docUrl: string) =>
  api.post<{ status: string }>(`/dash/documents/${docId}/update-notification`, {
    doc_url: docUrl,
  });

export const getKnowledgeBase = (params?: any) =>
  api.get("/dash/knowledge", { params });
export const createKnowledgeBaseItem = (data: any) =>
  api.post("/dash/knowledge", data);
export const updateKnowledgeBaseItem = (itemId: string, data: any) =>
  api.put(`/dash/knowledge/${itemId}`, data);
export const deleteKnowledgeBaseItem = (itemId: string) =>
  api.delete(`/dash/knowledge/${itemId}`, {
    validateStatus: function (status) {
      return status === 204; // Expect 204 No Content
    },
  });

export default api;
